import { Injectable, OnDestroy } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { environment } from '../../../../environments/environment';
import { NotificationDto } from '../../interfaces/notification';
import { NotificationService } from './notification.service';

@Injectable({ providedIn: 'root' })
export class NotificationHubService implements OnDestroy {
  private hubConnection: HubConnection | null = null;
  private retryDelay = 5_000;

  constructor(private notificationService: NotificationService) {}

  /**
   * Call once the JWT is available (e.g. after Keycloak login).
   * @param tokenFactory  Function that returns a fresh access token string.
   */
  connect(tokenFactory: () => string): void {
    if (this.hubConnection) return;

    // Le hub n'est pas sous /api : on retire ce suffixe de BASE_URL. Ancré en
    // fin de chaîne — un simple replace('/api', '') frappait la première
    // occurrence, soit le nom d'hôte lui-même en production
    // (https://api.skill2era.com/api → https:/.skill2era.com/api), URL que le
    // navigateur résolvait ensuite contre l'origine de la page.
    const hubBase = environment.BASE_URL.replace(/\/api\/?$/, '');

    this.hubConnection = new HubConnectionBuilder()
      .withUrl(`${hubBase}/hubs/notifications`, {
        accessTokenFactory: tokenFactory,
      })
      .configureLogging(LogLevel.Warning)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .build();

    // Le hub émet « notification » (SignalRNotificationPusher). L'écoute
    // portait sur « ReceiveNotification » : aucun message n'arrivait jamais,
    // et le temps réel semblait simplement inactif.
    this.hubConnection.on(
      'notification',
      (notification: NotificationDto) => {
        this.notificationService.prependNotification(notification);
      },
    );

    this.hubConnection.onreconnected(() => {
      this.notificationService.list().subscribe();
    });

    this.startConnection();
  }

  private startConnection(): void {
    if (!this.hubConnection) return;
    this.hubConnection
      .start()
      .then(() => {
        this.notificationService.list().subscribe();
      })
      .catch(() => {
        setTimeout(() => this.startConnection(), this.retryDelay);
      });
  }

  disconnect(): void {
    if (
      this.hubConnection &&
      this.hubConnection.state !== HubConnectionState.Disconnected
    ) {
      this.hubConnection.stop();
    }
    this.hubConnection = null;
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
